import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Check, Circle, Loader2, X } from 'lucide-react';
import { cn } from '$lib/lib/cn';

// Per-step status. Distinct from RunStatus (which describes a whole run) so
// the diff between "done" and "done with errors" reads at a glance and a
// failed stage halts the pipeline visually.
export type StageStatus = 'done' | 'done-with-errors' | 'running' | 'pending' | 'failed';

export interface Stage {
  // Stable identifier — used for the React key and as a data attribute on the
  // step node so e2e tests / screenshot diffs can target a single step.
  id: string;
  label: string;
  status: StageStatus;
  // Optional counter (e.g. "1,204 listings") rendered under the label.
  count?: ReactNode;
}

export interface StageProgressProps {
  stages: Stage[];
  // Optional aria label override for the wrapper. Defaults to the run-detail
  // pipeline label from i18n.
  ariaLabel?: string;
  className?: string;
}

interface StepVisual {
  ring: string;
  text: string;
  // Colour used by the connector LEAVING this step (i.e. drawn on its right
  // edge). On stacked layouts the connector is hidden.
  trailingConnector: string;
  icon: (props: { size: number; className?: string }) => ReactNode;
  ariaSuffixKey: string;
}

const stepVisuals: Record<StageStatus, StepVisual> = {
  done: {
    ring: 'bg-positive/15 border-positive text-positive',
    text: 'text-fg',
    trailingConnector: 'bg-positive',
    icon: ({ size, className }) => <Check size={size} className={className} aria-hidden />,
    ariaSuffixKey: 'admin.runDetail.stageDoneAriaLabel'
  },
  'done-with-errors': {
    ring: 'bg-warning/15 border-warning text-warning',
    text: 'text-fg',
    trailingConnector: 'bg-warning',
    icon: ({ size, className }) => <AlertTriangle size={size} className={className} aria-hidden />,
    // Falls back to the done aria label — stage-level "with errors" copy isn't
    // distinguished by i18n yet; the icon and tone carry the signal.
    ariaSuffixKey: 'admin.runDetail.stageDoneAriaLabel'
  },
  running: {
    ring: 'bg-info/15 border-info text-info',
    text: 'text-fg',
    trailingConnector: 'bg-rule-strong',
    icon: ({ size, className }) => (
      <Loader2 size={size} className={cn('motion-safe:animate-spin', className)} aria-hidden />
    ),
    ariaSuffixKey: 'admin.runDetail.stageRunningAriaLabel'
  },
  pending: {
    ring: 'bg-surface border-rule-strong text-fg-subtle',
    text: 'text-fg-muted',
    trailingConnector: 'bg-rule',
    icon: ({ size, className }) => <Circle size={size} className={className} aria-hidden />,
    ariaSuffixKey: 'admin.runDetail.stagePendingAriaLabel'
  },
  failed: {
    ring: 'bg-danger/15 border-danger text-danger',
    text: 'text-fg',
    trailingConnector: 'bg-rule',
    icon: ({ size, className }) => <X size={size} className={className} aria-hidden />,
    ariaSuffixKey: 'admin.runDetail.stageFailedAriaLabel'
  }
};

// Layout
// ------
// Narrow viewports (default): each step is a row — `[ring] [label/count]`.
// Wide viewports (sm+):       row of steps with the trailing connector inside
//                             each step. The ring is right-aligned in its
//                             cell and the connector occupies the rest of the
//                             cell, so consecutive cells produce
//                             `[…line][ring][…line][ring]…[ring]` with no gaps
//                             on the last step.
export function StageProgress({ stages, ariaLabel, className }: StageProgressProps) {
  const { t } = useTranslation();
  const groupLabel = ariaLabel ?? t('admin.runDetail.pipelineAriaLabel');

  return (
    <ol
      role="list"
      aria-label={groupLabel}
      className={cn(
        'flex flex-col gap-3',
        'sm:flex-row sm:items-start sm:gap-0',
        className
      )}
    >
      {stages.map((stage, idx) => {
        const visual = stepVisuals[stage.status];
        const isLast = idx === stages.length - 1;
        return (
          <li
            key={stage.id}
            data-stage-id={stage.id}
            data-stage-status={stage.status}
            aria-label={t(visual.ariaSuffixKey, { stage: stage.label })}
            className={cn(
              'flex items-start gap-3',
              // The last step doesn't grow — only the connector-bearing steps
              // need to flex so the trailing line can fill the remaining
              // space. This keeps the final ring + label aligned to the right
              // edge of the parent without an awkward dangling line.
              'sm:flex-col sm:items-stretch sm:gap-1.5',
              !isLast && 'sm:flex-1'
            )}
          >
            <div className="flex items-center sm:w-full">
              <span
                className={cn(
                  'grid h-6 w-6 shrink-0 place-items-center rounded-full border',
                  visual.ring
                )}
              >
                {visual.icon({ size: 12 })}
              </span>
              {!isLast && (
                <span
                  aria-hidden
                  className={cn('hidden sm:block sm:h-px sm:flex-1', visual.trailingConnector)}
                />
              )}
            </div>
            <div
              className={cn(
                'min-w-0',
                // Pin the label under the ring on wide layouts. The ring is
                // 24px wide and lives at the start of the row, so the label
                // box is constrained to roughly the same width — for the last
                // step we let it grow because there's no trailing connector.
                'sm:-ml-1 sm:max-w-[10rem]',
                isLast && 'sm:max-w-none'
              )}
            >
              <div className={cn('text-xs font-medium', visual.text)}>{stage.label}</div>
              {stage.count != null && (
                <div className="num text-2xs text-fg-muted">{stage.count}</div>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
